import React, { useState, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAttendance } from '../../contexts/AttendanceContext';
import { useLanguage } from '../../contexts/LanguageContext';

import { Avatar, AvatarFallback } from '../ui/avatar';
import PageTransition from '../PageTransition';
import BottomNav from './BottomNav';
import CameraCapture from '../CameraCapture';
import PullToRefresh from '../PullToRefresh';
import { toast } from 'sonner';

export default function Layout(): React.JSX.Element {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { todayStatus, checkIn, checkOut } = useAttendance();
  const [showCamera, setShowCamera] = useState(false);
  const [cameraMode, setCameraMode] = useState<'check_in' | 'check_out'>('check_in');

  const getInitials = (name: string): string =>
    name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

  const handleCameraFab = (): void => {
    if (todayStatus?.clockIn && !todayStatus?.clockOut) {
      setCameraMode('check_out');
    } else {
      setCameraMode('check_in');
    }
    setShowCamera(true);
  };

  const handleCameraCapture = async (photoDataUrl: string): Promise<void> => {
    setShowCamera(false);
    try {
      if (cameraMode === 'check_out') {
        await checkOut(photoDataUrl);
        toast.success(t.checkedOutToast, { description: t.checkedOutDesc });
      } else {
        await checkIn(photoDataUrl);
        toast.success(t.checkedInToast, { description: t.checkedInDesc });
      }
    } catch (error) {
      const key = cameraMode === 'check_out' ? t.checkOutFailed : t.checkInFailed;
      toast.error(key, { description: (error as Error).message });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <PullToRefresh />

      {/* ── Slim top header — logo + user info only ─────────────────────── */}
      <header
        className="sticky top-0 z-50 w-full border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="flex h-14 items-center justify-between px-4 max-w-2xl mx-auto">
          <div className="flex items-center">
            <img src="/logo-only.png" alt="Attendify" className="h-12 w-12 object-contain" />
            <span className="text-base font-bold text-foreground tracking-tight">Attendify</span>
          </div>

          <div className="flex items-center gap-2">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
                {getInitials(user?.fullName ?? 'U')}
              </AvatarFallback>
            </Avatar>
            <div className="hidden sm:block">
              <p className="text-sm font-medium text-foreground leading-tight">{user?.fullName}</p>
              <p className="text-xs text-muted-foreground">{user?.code}</p>
            </div>
          </div>
        </div>
      </header>

      {/* ── Main content ────────────────────────────────────────────── */}
      <main
        className="mx-auto px-4 pt-4 max-w-2xl"
        style={{ paddingBottom: 'calc(4.75rem + env(safe-area-inset-bottom, 0px))' }}
      >
        <PageTransition>
          <Outlet />
        </PageTransition>
      </main>

      {/* ── Fixed overlays — outside scroll container ─────────────────── */}
      <BottomNav onCameraFab={handleCameraFab} />

      <CameraCapture
        open={showCamera}
        onCapture={(photo) => void handleCameraCapture(photo)}
        onClose={() => setShowCamera(false)}
        title={cameraMode === 'check_out' ? t.checkOutCameraTitle : t.cameraTitle}
      />
    </div>
  );
}
